import type { Station } from '@/types'
import type { PlaylistEntry } from '@/lib/stream-metadata'
import { ref, computed, watch } from 'vue'
import { config } from '@/lib/config'
import {
  createStatsListener,
  stopStatsListener,
  playlist,
} from '@/lib/stream-metadata'
import {
  setMediaSessionActionHandler,
  setMediaSessionMetadata,
  setMediaSessionPlaybackState,
} from '@/lib/media-session'
import { useApi } from '@/lib/api'
export { playlist } from '@/lib/stream-metadata'

let audioEl: HTMLAudioElement
let reconnectTimer: ReturnType<typeof setTimeout> | undefined
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5

export const isLoading = ref<boolean>(false)
export const isPlaying = ref<boolean>(false)
export const isPaused = ref<boolean>(false)
export const hasError = ref<boolean>(false)
export const volume = ref<number>(config.value.volume)

watch(volume, (val) => {
  const clamped = Math.max(0, Math.min(1, val))
  if (audioEl) {
    audioEl.volume = clamped
  }
  config.value.volume = clamped
})
const playWhenLoaded = ref<boolean>(true)
const stationsApi = useApi()

export const play = () => {
  if (
    !audioEl ||
    !config.value.currentStation ||
    isPlaying.value === true ||
    isLoading.value === true
  ) {
    return
  }

  // If player was paused, go live
  if (isPaused.value === true) {
    setStation(config.value.currentStation)
    return
  }

  audioEl?.play()
}

export const pause = () => {
  if (
    !audioEl ||
    !config.value.currentStation ||
    isPlaying.value === false ||
    isPaused.value === true ||
    isLoading.value === true
  ) {
    return
  }

  stop()
}

const stop = () => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = undefined
  }
  reconnectAttempts = 0

  stopStatsListener()

  if (audioEl) {
    audioEl.pause()
    audioEl.removeAttribute('src')
    audioEl.load()
  }

  isLoading.value = false
  isPlaying.value = false
  isPaused.value = true
  setMediaSessionPlaybackState('paused')
}

const reconnect = () => {
  const station = config.value.currentStation
  if (!station) {
    return
  }

  reconnectAttempts++

  if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
    stop()
    hasError.value = true
    setMediaSessionPlaybackState('none')
    return
  }

  isLoading.value = true

  const delay = Math.min(reconnectAttempts * 2000, 10000)
  reconnectTimer = setTimeout(() => {
    if (config.value.currentStation?.id === station.id) {
      audioEl.currentTime = 0
      audioEl.src = `${station.stream_url}?ts=${Date.now()}`
      audioEl.load()
    }
  }, delay)
}

const init = (): HTMLAudioElement => {
  if (audioEl) {
    return audioEl
  }

  audioEl = new Audio()
  audioEl.volume = volume.value
  audioEl.setAttribute('preload', 'auto') // iOS 17

  audioEl.addEventListener('loadstart', () => {
    isLoading.value = true
    setMediaSessionMetadata(config.value.currentStation)
    setMediaSessionPlaybackState('none')
  })

  audioEl.addEventListener('canplaythrough', () => {
    isLoading.value = false

    if (playWhenLoaded.value === false) {
      playWhenLoaded.value = true
      return
    }

    audioEl.play()
  })

  audioEl.addEventListener('play', () => {
    isLoading.value = false
    isPlaying.value = true
    isPaused.value = false
    reconnectAttempts = 0
    setMediaSessionPlaybackState('playing')
  })

  audioEl.addEventListener('playing', () => {
    isLoading.value = false
    isPlaying.value = true
    isPaused.value = false
    reconnectAttempts = 0
    setMediaSessionPlaybackState('playing')
  })

  audioEl.addEventListener('pause', () => {
    isLoading.value = false
    isPlaying.value = false
    isPaused.value = true
    setMediaSessionPlaybackState('paused')
  })

  audioEl.addEventListener('waiting', () => {
    isLoading.value = true
  })

  audioEl.addEventListener('ended', () => {
    isPlaying.value = false
    isPaused.value = true
    setMediaSessionPlaybackState('none')
  })

  audioEl.addEventListener('error', () => {
    const error = audioEl.error

    // MEDIA_ERR_SRC_NOT_SUPPORTED: stream not found, DNS failure, unsupported format
    if (error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      stop()
      hasError.value = true
      setMediaSessionPlaybackState('none')
      return
    }

    // MEDIA_ERR_ABORTED: user-initiated, no action needed
    if (error?.code === MediaError.MEDIA_ERR_ABORTED) {
      return
    }

    // MEDIA_ERR_NETWORK, MEDIA_ERR_DECODE, or unknown: retry
    reconnect()
  })

  // Set action handlers for media session
  setMediaSessionActionHandler('play', play)
  setMediaSessionActionHandler('pause', pause)
  setMediaSessionActionHandler('stop', pause)

  return audioEl
}

export const setStation = (station: Station, play: boolean = true): Station => {
  init()
  stop()

  hasError.value = false
  config.value.currentStation = station

  playWhenLoaded.value = typeof play !== 'undefined' ? play : true

  audioEl.currentTime = 0 // iOS 17
  audioEl.src = `${station.stream_url}?ts=${Date.now()}`
  audioEl.load() // iOS 17

  // Start fetching metadata
  createStatsListener(station)

  // Knock local station endpoint
  try {
    stationsApi.ky.get(`stations/${station.name}/click/`)
  } catch (_e) {
    // Fail silently
  }

  return station
}

export const nowPlaying = computed<PlaylistEntry | undefined>(() => {
  if (!currentPlaylist.value || !config.value.currentStation) {
    return undefined
  }

  return playlist.value[config.value.currentStation.id][0]
})

export const currentPlaylist = computed<PlaylistEntry[] | undefined>(() => {
  return config.value.currentStation
    ? playlist.value[config.value.currentStation.id]
    : undefined
})
